import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Bell, CheckCheck, ChevronLeft, ChevronRight, Inbox, Loader2, RefreshCw, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { notificationService } from "../../services/notificationService";

function responseData(response) {
  return response?.data ?? response ?? {};
}

function recordsFrom(response) {
  const data = responseData(response);
  if (Array.isArray(data)) return data;
  return data.content ?? data.records ?? data.items ?? data.notifications ?? [];
}

function recordId(record) {
  return record.notificationId ?? record.notification_id ?? record.id;
}

function isViewed(record) {
  return Boolean(record.viewed ?? record.isViewed ?? record.read ?? record.isRead ?? record.viewedAt ?? record.viewed_at);
}

function messageOf(record) {
  return record.message ?? record.body ?? record.content ?? record.description ?? "";
}

export function NotificationsModal({ open, onOpenChange, onUnreadCountChange }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [pageInfo, setPageInfo] = useState({ totalPages: 1, last: true });
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
    dateStyle: "medium",
    timeStyle: "short",
  }), [i18n.language, i18n.resolvedLanguage]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await notificationService.getRecords({ page, size: 10, unreadOnly, type: "*" });
      const data = responseData(response);
      setRecords(recordsFrom(response));
      setPageInfo({
        totalPages: Number(data.totalPages ?? data.total_pages ?? 1),
        last: Boolean(data.last ?? page + 1 >= Number(data.totalPages ?? data.total_pages ?? 1)),
      });
    } catch (requestError) {
      setError(requestError.message || t("notifications.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, unreadOnly, t]);

  useEffect(() => {
    if (open) loadRecords();
  }, [open, loadRecords]);

  async function openRecord(record) {
    const id = recordId(record);
    setExpandedId((current) => current === id ? null : id);
    if (id == null && !isViewed(record)) return;
    setError("");
    try {
      if (!isViewed(record)) {
        await notificationService.markViewed(id);
        setRecords((current) => current.map((item) => recordId(item) === id ? { ...item, viewed: true, isViewed: true } : item));
        onUnreadCountChange?.((current) => Math.max(0, current - 1));
      }
    } catch (requestError) {
      setError(requestError.message || t("notifications.markViewedError"));
      return;
    }

    if (typeof record.link === "string" && record.link.trim()) {
      let destination;
      try {
        destination = new URL(record.link.trim(), window.location.origin);
      } catch {
        return;
      }
      if (destination.origin === window.location.origin) {
        navigate(`${destination.pathname}${destination.search}${destination.hash}`);
        onOpenChange(false);
      }
    }
  }

  async function markAllViewed() {
    setActionLoading(true);
    setError("");
    try {
      await notificationService.markAllViewed();
      onUnreadCountChange?.(0);
      if (unreadOnly) setRecords([]);
      else setRecords((current) => current.map((item) => ({ ...item, viewed: true, isViewed: true })));
    } catch (requestError) {
      setError(requestError.message || t("notifications.markAllError"));
    } finally {
      setActionLoading(false);
    }
  }

  async function deleteRecord(record) {
    const id = recordId(record);
    if (id == null) return;

    setDeletingIds((current) => new Set(current).add(id));
    setError("");
    try {
      await notificationService.delete(id);
      setRecords((current) => current.filter((item) => recordId(item) !== id));
      setExpandedId((current) => current === id ? null : current);
      if (!isViewed(record)) onUnreadCountChange?.((current) => Math.max(0, current - 1));
    } catch (requestError) {
      setError(requestError.message || "Unable to delete notification.");
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-white shadow-2xl focus:outline-none">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]"><Bell className="h-5 w-5" /></span>
              <div className="min-w-0">
                <DialogPrimitive.Description className="truncate text-sm text-[var(--color-text-secondary)]">{t("notifications.description")}</DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60" aria-label={t("notifications.close")}><X className="h-4 w-4" /></DialogPrimitive.Close>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-workspace)]/35 px-5 py-3">
            <div className="flex rounded-lg border border-[var(--color-border)] bg-white p-1 text-sm">
              {[true, false].map((value) => <button key={String(value)} type="button" onClick={() => { setUnreadOnly(value); setPage(0); setExpandedId(null); }} className={`rounded-md px-3 py-1.5 font-medium ${unreadOnly === value ? "bg-[var(--color-primary)] text-white" : "text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]"}`}>{t(value ? "notifications.unread" : "common.all")}</button>)}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={loadRecords} disabled={loading} className="grid h-9 w-9 place-items-center rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] disabled:opacity-50" aria-label={t("notifications.refresh")}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
              <button type="button" onClick={markAllViewed} disabled={actionLoading || records.length === 0} className="inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--color-primary)] px-3 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">{actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}{t("notifications.markAllRead")}</button>
            </div>
          </div>

          {error && <div role="alert" className="mx-5 mt-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
          <div className="min-h-64 flex-1 overflow-y-auto p-3 sm:p-5">
            {loading ? <div className="grid min-h-56 place-items-center text-[var(--color-text-secondary)]"><span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary)]" />{t("notifications.loading")}</span></div> : records.length === 0 ? <div className="grid min-h-56 place-items-center text-center"><div><Inbox className="mx-auto mb-3 h-10 w-10 text-[var(--color-text-secondary)]/60" /><p className="font-medium text-[var(--color-text-primary)]">{t("notifications.empty")}</p><p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t("notifications.emptyHint")}</p></div></div> : <ul className="space-y-2">{records.map((notifications, index) => {
              const id = recordId(notifications) ?? `notification-${index}`;
              const viewed = isViewed(notifications);
              const rawDate = notifications.createdOn;
              let formattedDate = "";
              if (rawDate) { const parsed = new Date(rawDate); if (!Number.isNaN(parsed.getTime())) formattedDate = dateFormatter.format(parsed); }
              const deleting = deletingIds.has(id);
              return <li key={id} className={`relative rounded-xl border transition hover:border-[var(--color-primary)]/40 hover:shadow-sm ${viewed ? "border-[var(--color-border)] bg-white" : "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/[0.04]"}`}>
                <button type="button" onClick={() => openRecord(notifications)} className="w-full p-4 pb-11 text-left" aria-expanded={expandedId === id}>
                  <div className="flex items-start gap-3"><span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${viewed ? "bg-transparent" : "bg-[var(--color-primary)]"}`} /><div className="min-w-0 flex-1">{notifications.notificationType && <span className="inline-block rounded-full bg-[var(--color-workspace)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-secondary)]">{notifications.notificationType}</span>}<p className={`${expandedId === id ? "whitespace-pre-wrap" : "line-clamp-2"} mt-2 text-sm leading-5 text-[var(--color-text-secondary)]`}>{messageOf(notifications)}</p>{formattedDate && <time dateTime={rawDate} className="mt-1 block text-xs text-[var(--color-text-secondary)]">{formattedDate}</time>}</div></div>
                </button>
                <button type="button" onClick={() => deleteRecord(notifications)} disabled={deleting} className="absolute bottom-2 right-2 grid h-8 w-8 place-items-center rounded-lg text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50" aria-label="Delete notification" title="Delete notification">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </li>;
            })}</ul>}
          </div>

          <div className="flex items-center justify-between border-t border-[var(--color-border)] px-5 py-3 text-sm text-[var(--color-text-secondary)]"><span>{t("notifications.page", { page: page + 1, total: Math.max(pageInfo.totalPages, 1) })}</span><div className="flex gap-2"><button type="button" disabled={page === 0 || loading} onClick={() => setPage((current) => Math.max(0, current - 1))} className="grid h-9 w-9 place-items-center rounded-lg border border-[var(--color-border)] hover:text-[var(--color-primary)] disabled:opacity-40" aria-label={t("pagination.previousPage")}><ChevronLeft className="h-4 w-4" /></button><button type="button" disabled={pageInfo.last || loading} onClick={() => setPage((current) => current + 1)} className="grid h-9 w-9 place-items-center rounded-lg border border-[var(--color-border)] hover:text-[var(--color-primary)] disabled:opacity-40" aria-label={t("pagination.nextPage")}><ChevronRight className="h-4 w-4" /></button></div></div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
